'use client'

import { type ComponentProps, createContext, type ReactNode, useContext, useState } from 'react'
import clsx from 'clsx'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon } from '../../icons/CheckIcon'
import { ChevronIcon } from '../../icons/Chevron'
import { WarningTriangleIcon } from '../../icons/WarningTriangleIcon'
import { xStack, yStack } from '../../patterns/stack'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import { tokens } from '../../theme/tokens.css'
import {
  content,
  fullWidth as fullWidthStyles,
  item,
  label,
  scrollButton,
  triggerChevron,
  triggerIcon,
  triggerSize,
} from './Select.css'
import { trigger } from './Select.css'

type Variant = 'neutral' | 'danger' | 'warning'

type SelectContentValue = {
  value?: string | null
  variant: Variant
  size: 'small' | 'medium' | 'large'
  loading?: boolean
}

const SelectContext = createContext<SelectContentValue | null>(null)

const useSelectContext = () => {
  const context = useContext(SelectContext)

  if (!context) {
    throw new Error('useSelectContext must be used within a Select')
  }

  return context
}

type Props = ComponentProps<typeof SelectPrimitive.Root> & {
  variant?: Variant
  size?: 'small' | 'medium' | 'large'
  loading?: boolean
}

function SelectRoot({
  children,
  defaultValue,
  value,
  onValueChange,
  size = 'medium',
  variant = 'neutral',
  loading = false,
  ...rest
}: Props) {
  const [internalValue, setInternalValue] = useState(defaultValue)

  const isControlled = value != null
  const resolvedValue = isControlled ? value : internalValue

  const handleValueChange = (value: string) => {
    if (!isControlled) {
      setInternalValue(value)
    }
    onValueChange?.(value)
  }

  return (
    <SelectContext.Provider value={{ value: resolvedValue, variant, size, loading }}>
      <SelectPrimitive.Root value={value} defaultValue={defaultValue} onValueChange={handleValueChange} {...rest}>
        {children}
      </SelectPrimitive.Root>
    </SelectContext.Provider>
  )
}

type TriggerProps = Omit<ComponentProps<typeof SelectPrimitive.Trigger>, 'children'> & {
  fullWidth?: boolean
  includeChevron?: boolean
  children?: ReactNode
}

function Trigger({ children, className, fullWidth, includeChevron = true, ...props }: TriggerProps) {
  const { value, variant, size, loading } = useSelectContext()

  const hasValue = value != null && value !== ''

  return (
    <SelectPrimitive.Trigger
      {...props}
      data-selected={hasValue}
      className={clsx(trigger[variant], triggerSize[size], { [fullWidthStyles]: !!fullWidth }, className)}
      disabled={props.disabled || loading}
    >
      {children}
      {loading && <LoadingDots color={tokens.colors.fills.opaque.primary} />}
      {!loading && includeChevron && <Chevron />}
    </SelectPrimitive.Trigger>
  )
}

function Chevron() {
  const { variant } = useSelectContext()

  return (
    <div className={xStack({ gap: 'xxxs', alignItems: 'center' })}>
      {(variant === 'warning' || variant === 'danger') && (
        <WarningTriangleIcon fill={'currentColor'} className={triggerIcon} />
      )}

      <SelectPrimitive.Icon>
        <ChevronIcon className={triggerChevron} size="1rem" color={tokens.colors.fills.opaque.primary} />
      </SelectPrimitive.Icon>
    </div>
  )
}

function Label({ children, className, ...props }: ComponentProps<'span'>) {
  const { value } = useSelectContext()
  const hasValue = value != null && value !== ''

  return (
    <>
      {/* Hidden element to maintain space */}
      {!hasValue && (
        <span className={clsx(label, className)} style={{ visibility: 'hidden' }} aria-hidden="true" {...props}>
          {children}
        </span>
      )}

      {/* Visible label that animates */}
      <span className={clsx(label, className)} {...props}>
        {children}
      </span>
    </>
  )
}

function SelectContent({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content position="popper" side="bottom" className={clsx(content, className)} {...rest}>
        {children}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectOptions({ className, children, ...rest }: ComponentProps<typeof SelectPrimitive.Viewport>) {
  return (
    <>
      <SelectPrimitive.ScrollUpButton className={scrollButton}>
        <ChevronUp />
      </SelectPrimitive.ScrollUpButton>

      <SelectPrimitive.Viewport className={clsx(yStack({ gap: 'xxs' }), className)} {...rest}>
        {children}
      </SelectPrimitive.Viewport>

      <SelectPrimitive.ScrollDownButton className={scrollButton}>
        <ChevronDown />
      </SelectPrimitive.ScrollDownButton>
    </>
  )
}

function SelectItem({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.Item>) {
  const { size } = useSelectContext()

  return (
    <SelectPrimitive.Item className={clsx(item[size], className)} {...rest}>
      {children}
    </SelectPrimitive.Item>
  )
}

function SelectItemText({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.ItemText>) {
  return (
    <SelectPrimitive.ItemText className={className} {...rest}>
      {children}
    </SelectPrimitive.ItemText>
  )
}

type SelectItemIndicatorProps = Omit<ComponentProps<typeof SelectPrimitive.ItemIndicator>, 'children'>
function SelectItemIndicator(props: SelectItemIndicatorProps) {
  return (
    <SelectPrimitive.ItemIndicator {...props}>
      <CheckIcon size="16px" />
    </SelectPrimitive.ItemIndicator>
  )
}

function Context({ children }: { children: (context: SelectContentValue) => ReactNode }) {
  const context = useSelectContext()
  return <>{children(context)}</>
}

export const Select = {
  Root: SelectRoot,
  Trigger: Trigger,
  Chevron: Chevron,
  Label: Label,
  Value: SelectPrimitive.Value,
  Content: SelectContent,
  Options: SelectOptions,
  Item: SelectItem,
  ItemText: SelectItemText,
  ItemIndicator: SelectItemIndicator,
  Context: Context,
}
